import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { type AppUserSession } from "@/schemas/auth/session";
import { type UserImageMedia, UserImageType } from "@/schemas/user/image";
import ProfileService, {
  UserProfileImageUpdateType,
} from "@/services/user/profile";
import universalUploadImage from "@/utils/image/universalUploadImage";

type BackgroundImageProps = {
  session: AppUserSession;
  onChangeBackground: (image: UserImageMedia) => void;
};

function BackgroundImage({
  session,
  onChangeBackground,
}: BackgroundImageProps): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  const updateProfileBackground = async (
    image: UserImageMedia,
  ): Promise<void> => {
    const profileService = new ProfileService(session);
    await profileService.updateProfile(
      UserProfileImageUpdateType.BACKGROUND,
      image,
    );
  };

  const handleChangeBackground = async (): Promise<void> => {
    await universalUploadImage({
      session,
      imageType: UserImageType.BACKGROUND_IMAGE,
      aspectRatio: { width: 4, height: 3 },
      compressionQuality: 0.7, // this is not zoom-able to user, keep it low
      onUploadStart: () => setIsLoading(true),
      onUploadSuccess: async (userImages) => {
        if (userImages && userImages.length > 0 && userImages[0]) {
          const backgroundImage = userImages[0];
          await updateProfileBackground(backgroundImage);
          onChangeBackground(backgroundImage);
        }
      },
      onUploadEnd: () => setIsLoading(false),
    });
  };

  return (
    <View style={styles.layout}>
      <TouchableOpacity
        onPress={handleChangeBackground}
        disabled={isLoading}
        style={styles.row}
        hitSlop={8}
      >
        {isLoading ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <>
            <MaterialCommunityIcons
              name="panorama-outline"
              size={14}
              color="#ddd"
            />
            <Text style={styles.uploadText}> Change Cover</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  layout: {
    padding: 10,
    alignSelf: "flex-start",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  uploadText: {
    color: "#ddd",
  },
});

export default BackgroundImage;
